import type { ChoiceItem } from "./fieldOptions";
import { toChoiceRows } from "./fieldOptions";

export type RemoteChoicesResult = {
  choices: ChoiceItem[];
  error: string | null;
};

function unwrapRecord(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const record = body as Record<string, unknown>;
  if (
    record.data &&
    typeof record.data === "object" &&
    !Array.isArray(record.data)
  ) {
    const nested = record.data as Record<string, unknown>;
    if (Array.isArray(nested.choices) || nested.error != null) return nested;
  }
  return record;
}

function readTrimmed(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

function toChoiceItems(value: unknown): ChoiceItem[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: ChoiceItem[] = [];

  for (const item of value) {
    if (typeof item === "string") {
      const text = item.trim();
      if (!text || seen.has(`s:${text}`)) continue;
      seen.add(`s:${text}`);
      result.push(text);
      continue;
    }

    if (!item || typeof item !== "object" || Array.isArray(item)) {
      const text = String(item ?? "").trim();
      if (!text || seen.has(`s:${text}`)) continue;
      seen.add(`s:${text}`);
      result.push(text);
      continue;
    }

    const record = item as Record<string, unknown>;
    const label = readTrimmed(record.label ?? record.name);
    const choiceValue = readTrimmed(record.value);

    if (label && choiceValue) {
      const key = `v:${choiceValue}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ label, value: choiceValue });
      continue;
    }

    const text = label || choiceValue;
    if (!text || seen.has(`s:${text}`)) continue;
    seen.add(`s:${text}`);
    result.push(text);
  }

  return result;
}

export function formatChoicePreview(item: ChoiceItem): string {
  const [row] = toChoiceRows([item]);
  if (!row) return "";
  if (row.label !== row.value) return `${row.label} (${row.value})`;
  return row.label;
}

export function normalizeRemoteChoices(body: unknown): RemoteChoicesResult {
  if (Array.isArray(body)) {
    return { choices: toChoiceItems(body), error: null };
  }

  const record = unwrapRecord(body);
  if (!record) {
    return { choices: [], error: "Некорректный ответ списка" };
  }

  const errorRaw = record.error;
  const error =
    errorRaw == null || errorRaw === ""
      ? null
      : String(errorRaw).trim() || null;
  const choices = toChoiceItems(record.choices);

  return { choices, error };
}
